import { useState, useEffect, ReactNode, useRef } from 'react';
import { SecondaryDrawer } from './SecondaryDrawer';
import type { IStaticLink, IAssetType, ICategory } from './types';
import NextImage from '../NextImage';
import { ImagesService } from 'service/images/imagesService';

import {
  DrawerWrapper,
  SubNav,
  NavLink,
  Texture,
  NavObject,
  LinkContainer,
  LintStyle,
  FeaturedLinkStyle,
  openDrawerStyle,
  CategoryList,
  textureStyle,
} from './styles';

import { LinksService } from 'service/links/linksService';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';
import { NextLink } from 'components/NextLink';

export const SideDrawer = ({
  open = false,
  staticLinks,
  secondaryAssests,
  navCategories,
  cardAdditonalInfo,
  id = 'header-sub-nav',
  trackingEvents,
  onLinkClick,
}: {
  open?: boolean;
  staticLinks?: IStaticLink[];
  secondaryAssests?: IAssetType[];
  navCategories?: ICategory[];
  cardAdditonalInfo?: any;
  id?: string;
  trackingEvents?: {
    secondary?: (mainNavItem: string, secondaryNavItem: string) => void;
    tertiary?: (mainNavItem: string, secondaryNavItem: string, tertiaryNavItem: string) => void;
  };
  onLinkClick: () => void;
}): ReactNode => {
  const { pathTranslations } = useGlobalContext();
  const [openSecond, openSideNav] = useState<boolean | undefined>(false);
  const [subCategory, setSubCategory] = useState<any>('');
  const [parentLink, setParentLink] = useState<string>();
  const [secondaryNavigationData, setSecondaryNavigationData] = useState<any>('');
  const [mainDiv, setMainDiv] = useState<any>();
  const [closeDiv, setcloseDiv] = useState<any>();

  const containerRef = useRef<HTMLDivElement>(null);

  const mainNavItem = cardAdditonalInfo.key;

  const getSubCategory = (category: any) => {
    const links = navCategories?.filter((item: any) => {
      let subCategoryitems;
      if (item.title === category) {
        subCategoryitems = item;
      }
      return subCategoryitems;
    })[0];
    let subCategoryLink;
    if (links?.subCategory?.length) {
      subCategoryLink = links.subCategory;
    }
    setParentLink(links?.category_link?.internalLink?.slug);
    return {
      subCategoryLink,
      parentLink: links?.category_link?.internalLink?.slug,
    };
  };

  const openSideNavHandler = (event: any) => {
    let subCategoryTitle = '';
    subCategoryTitle = event.target.dataset.category;
    const subCategoryData = getSubCategory(subCategoryTitle);
    setSecondaryNavigationData(subCategoryTitle);
    const selectedList = document.querySelectorAll('.selected-category');
    if (subCategoryData) {
      if (selectedList.length > 0) {
        for (let index = 0; index < selectedList.length; index++) {
          (selectedList[index] as HTMLElement).classList.remove('selected-category');
        }
      }
      event.currentTarget.parentElement.classList.add('selected-category');
    } else {
      for (let index = 0; index < selectedList.length; index++) {
        (selectedList[index] as HTMLElement).classList.remove('selected-category');
      }
    }
    if (open || subCategoryData) {
      const isCategory = navCategories?.some((navItem) => navItem?.title === subCategoryTitle);
      const isStatic = staticLinks?.some((navItem) => navItem?.displayName === subCategoryTitle);
      openSideNav(true);
      (isCategory || isStatic) && setSubCategory(subCategoryData);
    }
  };

  const closeSideNavHandler = () => {
    if (open) {
      openSideNav(false);
    }
  };

  const categoryNavKeyDown = () => {
    mainDiv?.addEventListener('keyup', (event: any) => {
      if (event.key === 'Escape') {
        const Target = event.currentTarget.getAttribute('data-parent');
        closeDiv.focus();
        const firstNavigation = document.querySelectorAll('.main-menu');
        for (let i = 0; i <= firstNavigation.length; i += 1) {
          const mainMenu = firstNavigation[i]?.getAttribute('data-subnav');
          if (mainMenu === Target) {
            (firstNavigation[i] as HTMLElement).focus();
          }
        }
      }
    });
  };

  // accessibility
  const onKeyDown = () => {
    const allTabs = document.querySelectorAll('.category-link');

    for (let i = 0; i <= allTabs.length; i += 1) {
      if (allTabs[i]) {
        allTabs[i].addEventListener(
          'keyup',
          (event: any) => {
            if (event.key === 'ArrowUp') {
              (allTabs[i - 1] as HTMLElement)?.focus();
            } else if (event.key === 'ArrowDown') {
              (allTabs[i + 1] as HTMLElement)?.focus();
            } else if (event.key === 'ArrowRight') {
              openSideNavHandler(event);
            }
          },
          false,
        );
      }
    }
  };

  const handleSecondaryDrawerLinkClick = () => {
    openSideNav(false);

    if (parentLink && containerRef.current) {
      const element: HTMLAnchorElement | null = containerRef.current.querySelector(
        `a[data-slug="${parentLink}"]`,
      );
      element?.focus();
    }
  };

  useEffect(() => {
    closeSideNavHandler();
    setMainDiv(containerRef?.current);
    const closeMainNav = document.getElementById('close-main-header');
    setcloseDiv(closeMainNav);
  }, []);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        openSideNav(false);
        setParentLink('');
      }
    };

    document.addEventListener('keyup', handleEscapeKey);
    return () => {
      document.removeEventListener('keyup', handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setSubCategory(undefined);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <DrawerWrapper
      className={open ? openDrawerStyle : ''}
      id="category-nav"
      onKeyDown={categoryNavKeyDown}
      data-parent={mainNavItem}
      ref={containerRef}
    >
      <SubNav>
        <LinkContainer className="category-navigation-wapper mx-20P my-10P">
          <ul className="mb-30 border-b border-heroCurve">
            {staticLinks &&
              staticLinks.map((item: any, index: number) => (
                <CategoryList
                  className="category-navigation-list"
                  onClick={() => {
                    trackingEvents?.secondary &&
                      trackingEvents?.secondary(mainNavItem, item.displayName);
                  }}
                  key={item.id}
                  role="button"
                >
                  <NavLink
                    tag="div"
                    type="xs"
                    className={FeaturedLinkStyle}
                    onMouseOver={openSideNavHandler}
                    data-category={item.displayName}
                  >
                    <NextLink
                      id={index === 0 ? 'first-category-nav' : ''}
                      className="category-link w-full inline-block focus-visible:outline-1"
                      target={item.openInNewTab ? '_blank' : '_self'}
                      tabIndex={0}
                      href={
                        LinksService.getInternalLinks({ item, pathTranslations }) || item.url || ''
                      }
                      onClick={() => {
                        trackingEvents?.secondary &&
                          trackingEvents?.secondary(mainNavItem, item.displayName);

                        onLinkClick();
                      }}
                      onKeyDown={onKeyDown}
                      data-category={item.displayName}
                      area-label={item.displayName}
                      onBlur={() => {
                        if (!navCategories && staticLinks.length === index + 1) {
                          onLinkClick();
                        }
                      }}
                    >
                      {item.displayName}
                    </NextLink>
                  </NavLink>
                </CategoryList>
              ))}
          </ul>
          <ul className="mt-30">
            {navCategories &&
              navCategories.map((item: any, index: number) => (
                <CategoryList className="category-navigation-list" key={`${id + index}list`}>
                  <NavLink
                    tag="div"
                    type="xxxs"
                    className={LintStyle}
                    data-category={item.title}
                    onMouseEnter={openSideNavHandler}
                    onKeyDown={onKeyDown}
                  >
                    <NextLink
                      className="category-link w-full inline-block focus-visible:outline-1"
                      data-category={item.title}
                      data-slug={item.category_link?.internalLink?.slug}
                      href={
                        LinksService.getInternalLinks({
                          item: item?.category_link,
                          pathTranslations,
                        }) ||
                        item?.category_link?.url ||
                        ''
                      }
                      tabIndex={0}
                      onMouseOver={openSideNavHandler}
                      onClick={() => {
                        trackingEvents?.secondary &&
                          trackingEvents?.secondary(mainNavItem, item.title);

                        handleSecondaryDrawerLinkClick();
                        onLinkClick();
                      }}
                      area-label={item.title}
                      onBlur={() => {
                        if (navCategories.length === index + 1) {
                          onLinkClick();
                        }
                      }}
                    >
                      {item.title}
                    </NextLink>
                    <SecondaryDrawer
                      open={openSecond}
                      subCategoryNavData={subCategory}
                      parentCategorySlug={parentLink as string}
                      fullData={cardAdditonalInfo}
                      dataTrackingSecNav={secondaryNavigationData}
                      trackingEvents={{
                        tertiary: trackingEvents?.tertiary,
                      }}
                      onLinkClick={handleSecondaryDrawerLinkClick}
                    />
                  </NavLink>
                </CategoryList>
              ))}
          </ul>
        </LinkContainer>
        {secondaryAssests?.length ? (
          <Texture className={textureStyle}>
            {secondaryAssests.map((item: any, index: number) => (
              <div key={`${id + index}image`}>
                <>
                  {/* get the first image in the array for the background */}
                  {index === 0 && (
                    <NextImage
                      sources={ImagesService.getPictureSources(item?.asset?.[0])}
                      className="h-auto w-full max-w-none absolute top-0"
                      alt=""
                      role="presentation"
                    />
                  )}
                </>
                <>
                  {/* get the second image in the array for the foreground */}
                  {index === 1 && (
                    <NavObject>
                      <NextImage
                        sources={ImagesService.getPictureSources(item?.asset?.[0])}
                        className="w-full absolute top-0"
                        alt=""
                        role="presentation"
                      />
                    </NavObject>
                  )}
                </>
              </div>
            ))}
          </Texture>
        ) : null}
      </SubNav>
    </DrawerWrapper>
  );
};
